/**
 * AddPasskeyPill.jsx — Subtle header pill that invites the user to set up a
 * passkey when they don't have one yet. Self-hides once enrolled, and hides
 * entirely on browsers without WebAuthn support.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import {
  isPasskeySupported,
  listPasskeys,
  enrollPasskey,
} from '../utils/passkeyApi';

const detectDefaultName = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  if (/iPhone|iPad/.test(ua)) return 'iPhone (Face/Touch ID)';
  if (/Mac/.test(ua)) return 'Mac (Touch ID)';
  if (/Android/.test(ua)) return 'Android device';
  if (/Windows/.test(ua)) return 'Windows (Hello)';
  return 'This device';
};

const AddPasskeyPill = ({ inline = false }) => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    if (!isPasskeySupported()) {
      setShow(false);
      return;
    }
    try {
      const d = await listPasskeys();
      setShow(!d.passkeys || d.passkeys.length === 0);
    } catch {
      setShow(false);
    }
  }, []);

  useEffect(() => {
    check();
    const onChange = () => check();
    window.addEventListener('passkey:changed', onChange);
    return () => window.removeEventListener('passkey:changed', onChange);
  }, [check]);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    const t = toast.loading('Waiting for biometric / device unlock...');
    try {
      await enrollPasskey(detectDefaultName());
      toast.success('✅ Passkey saved.', { id: t });
      setShow(false);
      window.dispatchEvent(new CustomEvent('passkey:changed'));
    } catch (e) {
      const msg = /NotAllowedError|cancell/i.test(e.message || '')
        ? 'Cancelled.'
        : (e.message || 'Enrolment failed');
      toast.error(msg, { id: t });
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  const base = inline
    ? 'text-[11px] px-2.5 py-1 rounded-full font-semibold flex items-center gap-1.5'
    : 'w-full text-sm px-4 py-3 rounded-lg font-semibold flex items-center gap-2';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={`${base} bg-fuchsia-600/15 text-fuchsia-200 border border-fuchsia-500/40 hover:bg-fuchsia-600/25 transition-colors disabled:opacity-60`}
      data-testid="add-passkey-pill"
      aria-label="Add a passkey for faster sign-in"
    >
      <KeyRound className="w-3.5 h-3.5" />
      <span className={inline ? 'hidden sm:inline' : ''}>
        {busy ? 'Enrolling...' : 'Add passkey'}
      </span>
    </button>
  );
};

export default AddPasskeyPill;
